import { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import {
  Datagrid,
  Filter,
  FunctionField,
  List,
  SelectInput,
  TextInput,
  useNotify,
  useRecordContext,
  useRefresh,
  useTranslate,
} from 'react-admin';
import { CustomBreadcrumbs } from 'src/components/custom-breadcrumbs';
import { ConfirmDialog } from 'src/components/custom-dialog';
import { DashboardContent } from 'src/layouts/dashboard';
import { PermissionGuard } from 'src/routes/components/permission-guard';
import { paths } from 'src/routes/paths';
import ErrorPage403 from 'src/pages/error/403';
import {
  StIntel,
  stErrorMessage,
  stPublishIntel,
  stUpdateIntel,
  stDeleteIntel,
} from '../../../services/socialTradingService';
import { Resources } from '../../resource';
import {
  StActionButton,
  StActionRow,
  StIdCell,
  StStatusChip,
  stFormatDateTime,
  stText,
} from '../StListUi';
import { I18N_LANGS, I18N_LANG_LABELS, I18nLangCode, formLang, langLabel, storageLang } from '../i18nLang';
import './init';

const TITLE_MAX = 40;
const SUMMARY_MAX = 80;
const TAGS_PREVIEW = 3;

const INTEL_TYPE_CHOICES = [
  { id: 'major_crypto', name: 'major_crypto' },
  { id: 'global_news', name: 'global_news' },
  { id: 'cn_a_share', name: 'cn_a_share' },
  { id: 'us_stock', name: 'us_stock' },
  { id: 'hk_stock', name: 'hk_stock' },
  { id: 'precious_metals', name: 'precious_metals' },
  { id: 'agent_signal', name: 'agent_signal' },
];

const INTEL_SENTIMENT_CHOICES = [
  { id: 'BULLISH' as const, nameKey: 'module.socialTrading.sentimentBullish' },
  { id: 'BEARISH' as const, nameKey: 'module.socialTrading.sentimentBearish' },
  { id: 'NEUTRAL' as const, nameKey: 'module.socialTrading.sentimentNeutral' },
];

const INTEL_SENTIMENT_COLOR: Record<string, 'success' | 'error' | 'default'> = {
  BULLISH: 'success',
  BEARISH: 'error',
  NEUTRAL: 'default',
};

type PublishForm = {
  type: string;
  title: string;
  summary: string;
  sourceName: string;
  sourceUrl: string;
  language: I18nLangCode;
  sentiment: 'BULLISH' | 'BEARISH' | 'NEUTRAL';
  tags: string;
};

const emptyPublishForm = (): PublishForm => ({
  type: 'major_crypto',
  title: '',
  summary: '',
  sourceName: '',
  sourceUrl: '',
  language: 'zh',
  sentiment: 'NEUTRAL',
  tags: '',
});

function truncate(text: string, max: number): { short: string; truncated: boolean } {
  const s = (text || '').trim();
  if (s.length <= max) return { short: s, truncated: false };
  return { short: `${s.slice(0, max)}…`, truncated: true };
}

function IntelSentimentChip({
  sentiment,
  t,
}: {
  sentiment?: string;
  t: (key: string) => string;
}) {
  const choice = INTEL_SENTIMENT_CHOICES.find((c) => c.id === sentiment);
  return (
    <Chip
      size="small"
      color={INTEL_SENTIMENT_COLOR[sentiment || ''] || 'default'}
      label={t(choice?.nameKey || 'module.socialTrading.sentimentNeutral')}
    />
  );
}

function IntelExpand() {
  const record = useRecordContext<StIntel>();
  if (!record) return null;
  return (
    <Box sx={{ p: 2, maxWidth: 960 }}>
      <Typography variant="subtitle2" gutterBottom>
        {record.title}
      </Typography>
      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
        {record.summary}
      </Typography>
      {record.tags?.length ? (
        <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" sx={{ mt: 1.5 }}>
          {record.tags.map((tag) => (
            <Chip key={tag} size="small" label={`#${tag}`} />
          ))}
        </Stack>
      ) : null}
    </Box>
  );
}

const ListFilter = (props: any) => {
  const t = useTranslate();
  return (
    <Filter {...(props || {})}>
      <SelectInput
        source="type"
        label={t('module.socialTrading.colType')}
        choices={INTEL_TYPE_CHOICES}
        alwaysOn
        resettable
        emptyText={t('module.socialTrading.filterAll')}
      />
      <TextInput source="tag" label={t('module.socialTrading.filterTag')} alwaysOn resettable />
      <TextInput source="intelId" label={t('module.socialTrading.colIntelId')} alwaysOn resettable />
      <TextInput source="keyword" label={t('module.socialTrading.filterKeyword')} alwaysOn resettable />
      <SelectInput
        source="language"
        label={t('module.socialTrading.publishIntelLanguage')}
        choices={[
          { id: 'zh', name: 'zh' },
          { id: 'en', name: 'en' },
        ]}
        alwaysOn
        resettable
        emptyText={t('module.socialTrading.filterAll')}
      />
    </Filter>
  );
};

export default function SocialTradingIntelList() {
  const t = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const [detail, setDetail] = useState<StIntel | null>(null);
  const [publishOpen, setPublishOpen] = useState(false);
  const [publishForm, setPublishForm] = useState<PublishForm>(emptyPublishForm);
  const [publishing, setPublishing] = useState(false);
  const [editTarget, setEditTarget] = useState<StIntel | null>(null);
  const [editForm, setEditForm] = useState<PublishForm>(emptyPublishForm);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<StIntel | null>(null);
  const [deleting, setDeleting] = useState(false);

  const typeLabel = useMemo(() => {
    const map = new Map(INTEL_TYPE_CHOICES.map((c) => [c.id, c.name]));
    return (code: string) => map.get(code) || code;
  }, []);

  const submitPublish = async () => {
    if (!publishForm.title.trim() || !publishForm.summary.trim() || !publishForm.sourceName.trim()) {
      notify(t('module.socialTrading.publishIntelNeedFields'), { type: 'warning' });
      return;
    }
    const tags = publishForm.tags
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    setPublishing(true);
    try {
      await stPublishIntel({
        type: publishForm.type,
        title: publishForm.title.trim(),
        summary: publishForm.summary.trim(),
        sourceName: publishForm.sourceName.trim(),
        sourceUrl: publishForm.sourceUrl.trim() || undefined,
        language: storageLang(publishForm.language),
        sentiment: publishForm.sentiment,
        tags: tags.length ? tags : undefined,
      });
      setPublishOpen(false);
      setPublishForm(emptyPublishForm());
      notify(t('module.socialTrading.publishIntelOk'), { type: 'success' });
      refresh();
    } catch (e: unknown) {
      notify(stErrorMessage(e), { type: 'error' });
    } finally {
      setPublishing(false);
    }
  };

  const openEdit = (row: StIntel) => {
    setEditTarget(row);
    setEditForm({
      type: row.type,
      title: row.title,
      summary: row.summary,
      sourceName: row.sourceName,
      sourceUrl: row.sourceUrl || '',
      language: formLang(row.language),
      sentiment: row.sentiment || 'NEUTRAL',
      tags: (row.tags || []).join(', '),
    });
  };

  const submitEdit = async () => {
    if (!editTarget) return;
    if (!editForm.title.trim() || !editForm.summary.trim() || !editForm.sourceName.trim()) {
      notify(t('module.socialTrading.publishIntelNeedFields'), { type: 'warning' });
      return;
    }
    const tags = editForm.tags
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    setSaving(true);
    try {
      await stUpdateIntel(editTarget.intelId, {
        type: editForm.type,
        title: editForm.title.trim(),
        summary: editForm.summary.trim(),
        sourceName: editForm.sourceName.trim(),
        sourceUrl: editForm.sourceUrl.trim() || undefined,
        language: storageLang(editForm.language),
        sentiment: editForm.sentiment,
        tags: tags.length ? tags : undefined,
      });
      setEditTarget(null);
      notify(t('module.socialTrading.editIntelOk'), { type: 'success' });
      refresh();
    } catch (e: unknown) {
      notify(stErrorMessage(e), { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await stDeleteIntel(deleteTarget.intelId);
      setDeleteTarget(null);
      notify(t('module.socialTrading.deleteIntelOk'), { type: 'success' });
      refresh();
    } catch (e: unknown) {
      notify(stErrorMessage(e), { type: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <PermissionGuard resource="st-intel" action="read" fallback={<ErrorPage403 />}>
      <DashboardContent maxWidth={false}>
        <CustomBreadcrumbs
          heading={t('module.socialTrading.intelList')}
          links={[
            { name: t('module.socialTrading.title'), href: paths.socialTrading.workbench.root },
            { name: t('module.socialTrading.groupIntel') },
            { name: t('module.socialTrading.intelList') },
          ]}
          action={
            <PermissionGuard resource="st-intel" action="create" fallback={null}>
              <Button size="small" variant="contained" onClick={() => setPublishOpen(true)}>
                {t('module.socialTrading.publishIntel')}
              </Button>
            </PermissionGuard>
          }
        />

        <List
          resource={Resources.socialTradingIntel}
          title=" "
          actions={false}
          empty={false}
          perPage={20}
          exporter={false}
          filters={<ListFilter />}
          sx={{ mt: 2 }}
        >
          <Box sx={{ overflow: 'auto' }}>
            <Datagrid
              rowClick={false}
              bulkActionButtons={false}
              expand={IntelExpand}
              empty={
                <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                  {t('module.socialTrading.emptyIntel')}
                </Typography>
              }
            >
              <FunctionField
                label={t('module.socialTrading.colIntelId')}
                render={(row: StIntel) => <StIdCell value={row.intelId} head={12} tail={6} />}
              />
              <FunctionField
                label={t('module.socialTrading.colType')}
                render={(row: StIntel) => <StStatusChip status={typeLabel(row.type)} />}
              />
              <FunctionField
                label={t('module.socialTrading.publishIntelLanguage')}
                render={(row: StIntel) => {
                  if (!row.language) return stText('');
                  const code = storageLang(formLang(row.language));
                  const full = langLabel(row.language);
                  return (
                    <Tooltip title={full}>
                      <Typography variant="body2" component="span" noWrap sx={{ display: 'inline-block' }}>
                        {code}
                      </Typography>
                    </Tooltip>
                  );
                }}
              />
              <FunctionField
                label={t('module.socialTrading.colSentiment')}
                render={(row: StIntel) => <IntelSentimentChip sentiment={row.sentiment} t={t} />}
              />
              <FunctionField
                label={t('module.socialTrading.colTitle')}
                render={(row: StIntel) => {
                  const { short } = truncate(row.title, TITLE_MAX);
                  return (
                    <Typography variant="body2" noWrap title={row.title} sx={{ maxWidth: 220 }}>
                      {stText(short)}
                    </Typography>
                  );
                }}
              />
              <FunctionField
                label={t('module.socialTrading.colSummary')}
                render={(row: StIntel) => {
                  const { short } = truncate(row.summary, SUMMARY_MAX);
                  return (
                    <Typography variant="body2" noWrap title={row.summary} sx={{ maxWidth: 280 }}>
                      {stText(short)}
                    </Typography>
                  );
                }}
              />
              <FunctionField
                label={t('module.socialTrading.colTags')}
                render={(row: StIntel) => {
                  const tags = row.tags || [];
                  if (!tags.length) return stText('');
                  const shown = tags.slice(0, TAGS_PREVIEW);
                  const rest = tags.length - shown.length;
                  return (
                    <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" sx={{ maxWidth: 200 }}>
                      {shown.map((tag) => (
                        <Chip key={tag} size="small" label={`#${tag}`} />
                      ))}
                      {rest > 0 ? <Chip size="small" variant="outlined" label={`+${rest}`} /> : null}
                    </Stack>
                  );
                }}
              />
              <FunctionField
                label={t('module.socialTrading.colSource')}
                render={(row: StIntel) =>
                  row.sourceUrl ? (
                    <Link href={row.sourceUrl} target="_blank" rel="noopener noreferrer" underline="hover">
                      {stText(row.sourceName)}
                    </Link>
                  ) : (
                    stText(row.sourceName)
                  )
                }
              />
              <FunctionField
                label={t('module.socialTrading.colPublishedAt')}
                render={(row: StIntel) => {
                  if (row.publishedAt === null || row.publishedAt === undefined || row.publishedAt === '') {
                    return stText('');
                  }
                  const d = dayjs(row.publishedAt);
                  const text = d.isValid() ? d.format('YYYY-MM-DD HH:mm') : String(row.publishedAt);
                  return (
                    <Typography variant="body2" component="span" noWrap sx={{ display: 'inline-block' }}>
                      {text}
                    </Typography>
                  );
                }}
              />
              <FunctionField
                label={t('module.socialTrading.actions')}
                textAlign="right"
                render={(row: StIntel) => (
                  <StActionRow>
                    <StActionButton onClick={() => setDetail(row)}>
                      {t('module.socialTrading.detail')}
                    </StActionButton>
                    <PermissionGuard resource="st-intel" action="update" fallback={null}>
                      <StActionButton onClick={() => openEdit(row)}>
                        {t('module.socialTrading.editIntel')}
                      </StActionButton>
                      <StActionButton onClick={() => setDeleteTarget(row)}>
                        {t('module.socialTrading.deleteIntel')}
                      </StActionButton>
                    </PermissionGuard>
                  </StActionRow>
                )}
              />
            </Datagrid>
          </Box>
        </List>

        <Dialog open={!!detail} onClose={() => setDetail(null)} fullWidth maxWidth="md">
          <DialogTitle>{t('module.socialTrading.intelDetail')}</DialogTitle>
          <DialogContent>
            {detail && (
              <Stack spacing={1.5} sx={{ mt: 0.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                  {detail.intelId}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                  <StStatusChip status={typeLabel(detail.type)} />
                  <IntelSentimentChip sentiment={detail.sentiment} t={t} />
                  <Typography variant="body2" color="text.secondary">
                    {langLabel(detail.language)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {stFormatDateTime(detail.publishedAt)}
                  </Typography>
                </Stack>
                <Typography variant="h6">{detail.title}</Typography>
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                  {detail.summary}
                </Typography>
                {detail.tags?.length ? (
                  <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                    {detail.tags.map((tag) => (
                      <Chip key={tag} size="small" label={`#${tag}`} />
                    ))}
                  </Stack>
                ) : null}
                <Typography variant="body2">
                  {t('module.socialTrading.colSource')}:{' '}
                  {detail.sourceUrl ? (
                    <Link href={detail.sourceUrl} target="_blank" rel="noopener noreferrer">
                      {detail.sourceName}
                    </Link>
                  ) : (
                    detail.sourceName
                  )}
                </Typography>
              </Stack>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDetail(null)}>{t('module.socialTrading.close')}</Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={publishOpen}
          onClose={() => !publishing && setPublishOpen(false)}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>{t('module.socialTrading.publishIntel')}</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                select
                label={t('module.socialTrading.colType')}
                value={publishForm.type}
                onChange={(e) => setPublishForm((f) => ({ ...f, type: e.target.value }))}
                fullWidth
                size="small"
              >
                {INTEL_TYPE_CHOICES.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label={t('module.socialTrading.colSentiment')}
                value={publishForm.sentiment}
                onChange={(e) =>
                  setPublishForm((f) => ({
                    ...f,
                    sentiment: e.target.value as PublishForm['sentiment'],
                  }))
                }
                fullWidth
                size="small"
                required
              >
                {INTEL_SENTIMENT_CHOICES.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {t(c.nameKey)}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label={t('module.socialTrading.colTitle')}
                value={publishForm.title}
                onChange={(e) => setPublishForm((f) => ({ ...f, title: e.target.value }))}
                fullWidth
                size="small"
                required
              />
              <TextField
                label={t('module.socialTrading.colSummary')}
                value={publishForm.summary}
                onChange={(e) => setPublishForm((f) => ({ ...f, summary: e.target.value }))}
                fullWidth
                size="small"
                required
                multiline
                minRows={3}
              />
              <TextField
                label={t('module.socialTrading.colSource')}
                value={publishForm.sourceName}
                onChange={(e) => setPublishForm((f) => ({ ...f, sourceName: e.target.value }))}
                fullWidth
                size="small"
                required
              />
              <TextField
                label={t('module.socialTrading.publishIntelSourceUrl')}
                value={publishForm.sourceUrl}
                onChange={(e) => setPublishForm((f) => ({ ...f, sourceUrl: e.target.value }))}
                fullWidth
                size="small"
                placeholder="https://"
              />
              <TextField
                select
                label={t('module.socialTrading.publishIntelLanguage')}
                value={publishForm.language}
                onChange={(e) =>
                  setPublishForm((f) => ({ ...f, language: e.target.value as I18nLangCode }))
                }
                fullWidth
                size="small"
                required
              >
                {I18N_LANGS.map((code) => (
                  <MenuItem key={code} value={code}>
                    {I18N_LANG_LABELS[code]}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label={t('module.socialTrading.colTags')}
                value={publishForm.tags}
                onChange={(e) => setPublishForm((f) => ({ ...f, tags: e.target.value }))}
                fullWidth
                size="small"
                helperText={t('module.socialTrading.publishIntelTagsHint')}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPublishOpen(false)} disabled={publishing}>
              {t('module.socialTrading.close')}
            </Button>
            <Button variant="contained" onClick={submitPublish} disabled={publishing}>
              {publishing ? t('module.socialTrading.publishingIntel') : t('module.socialTrading.publishIntel')}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={!!editTarget} onClose={() => !saving && setEditTarget(null)} fullWidth maxWidth="sm">
          <DialogTitle>{t('module.socialTrading.editIntel')}</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                label={t('module.socialTrading.colIntelId')}
                value={editTarget?.intelId || ''}
                fullWidth
                size="small"
                InputProps={{ readOnly: true }}
              />
              <TextField
                label={t('module.socialTrading.colPublishedAt')}
                value={editTarget ? stFormatDateTime(editTarget.publishedAt) : ''}
                fullWidth
                size="small"
                InputProps={{ readOnly: true }}
              />
              <TextField
                select
                label={t('module.socialTrading.colType')}
                value={editForm.type}
                onChange={(e) => setEditForm((f) => ({ ...f, type: e.target.value }))}
                fullWidth
                size="small"
              >
                {INTEL_TYPE_CHOICES.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label={t('module.socialTrading.colSentiment')}
                value={editForm.sentiment}
                onChange={(e) =>
                  setEditForm((f) => ({
                    ...f,
                    sentiment: e.target.value as PublishForm['sentiment'],
                  }))
                }
                fullWidth
                size="small"
                required
              >
                {INTEL_SENTIMENT_CHOICES.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {t(c.nameKey)}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label={t('module.socialTrading.colTitle')}
                value={editForm.title}
                onChange={(e) => setEditForm((f) => ({ ...f, title: e.target.value }))}
                fullWidth
                size="small"
                required
              />
              <TextField
                label={t('module.socialTrading.colSummary')}
                value={editForm.summary}
                onChange={(e) => setEditForm((f) => ({ ...f, summary: e.target.value }))}
                fullWidth
                size="small"
                required
                multiline
                minRows={3}
              />
              <TextField
                label={t('module.socialTrading.colSource')}
                value={editForm.sourceName}
                onChange={(e) => setEditForm((f) => ({ ...f, sourceName: e.target.value }))}
                fullWidth
                size="small"
                required
              />
              <TextField
                label={t('module.socialTrading.publishIntelSourceUrl')}
                value={editForm.sourceUrl}
                onChange={(e) => setEditForm((f) => ({ ...f, sourceUrl: e.target.value }))}
                fullWidth
                size="small"
                placeholder="https://"
              />
              <TextField
                select
                label={t('module.socialTrading.publishIntelLanguage')}
                value={editForm.language}
                onChange={(e) =>
                  setEditForm((f) => ({ ...f, language: e.target.value as I18nLangCode }))
                }
                fullWidth
                size="small"
                required
              >
                {I18N_LANGS.map((code) => (
                  <MenuItem key={code} value={code}>
                    {I18N_LANG_LABELS[code]}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label={t('module.socialTrading.colTags')}
                value={editForm.tags}
                onChange={(e) => setEditForm((f) => ({ ...f, tags: e.target.value }))}
                fullWidth
                size="small"
                helperText={t('module.socialTrading.publishIntelTagsHint')}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditTarget(null)} disabled={saving}>
              {t('module.socialTrading.close')}
            </Button>
            <Button variant="contained" onClick={submitEdit} disabled={saving}>
              {saving ? t('module.socialTrading.savingIntel') : t('module.socialTrading.editIntel')}
            </Button>
          </DialogActions>
        </Dialog>

        <ConfirmDialog
          open={!!deleteTarget}
          onClose={() => !deleting && setDeleteTarget(null)}
          title={t('module.socialTrading.deleteIntel')}
          content={t('module.socialTrading.confirmDeleteIntel')}
          action={
            <Button variant="contained" color="error" disabled={deleting} onClick={confirmDelete}>
              {t('module.socialTrading.confirmDelete')}
            </Button>
          }
        />
      </DashboardContent>
    </PermissionGuard>
  );
}
